import api from '@/factories/api';
import Axios from 'axios';
import studentdbApi, { getStudentDbBaseURL } from '@/factories/studentdbApi';

// In-flight token request — deduplicate concurrent fetches
let _tokenPromise = null;

async function fetchConveyourJwt() {
  const data = await api({ baseURL: '/' }).post(
    'plugin/vmc/users/studentdb_token'
  );
  return data?.token ?? null;
}

async function exchangeForStudentDbToken(conveyourToken) {
  const response = await Axios.post(`${getStudentDbBaseURL()}/api/auth/token`, {
    token: conveyourToken,
  });
  return response.data?.token ?? null;
}

function buildSortParam(sort) {
  if (!sort.field) return undefined;
  return { [sort.field]: sort.direction === 'desc' ? -1 : 1 };
}

function buildFilterParams(f) {
  if (!f) return {};
  const filter = {};
  if (f.gender) filter.gender = f.gender;
  if (f.levels?.length) filter.levels = f.levels;
  if (f.graduationYears?.length) filter.graduationYears = f.graduationYears;
  if (f.sports?.length) filter.sports = f.sports;
  if (f.minTeamsCount != null) filter.minTeamsCount = f.minTeamsCount;
  return Object.keys(filter).length ? { filter } : {};
}

export default {
  async fetchToken({ state, commit }) {
    if (state.token) return state.token;
    if (_tokenPromise) return _tokenPromise;

    _tokenPromise = (async () => {
      try {
        const conveyourToken = await fetchConveyourJwt();
        if (!conveyourToken) return null;

        const token = await exchangeForStudentDbToken(conveyourToken);
        commit('setToken', token);
        return token;
      } catch {
        return null;
      } finally {
        _tokenPromise = null;
      }
    })();

    return _tokenPromise;
  },

  async fetchSummary({ commit, state, rootGetters }) {
    const isAdmin = rootGetters['users/isAdmin'];
    if (state.summary != null && isAdmin) return;

    commit('setSummaryLoading', true);
    try {
      const params = isAdmin ? {} : buildFilterParams(state.studentFilter);
      const data = await studentdbApi().get('/api/reports/summary', { params });
      commit('setSummary', Array.isArray(data) ? data[0] : data);
    } finally {
      commit('setSummaryLoading', false);
    }
  },

  async fetchStates({ commit, state }) {
    if (state.states.length) return;

    commit('setStatesLoading', true);
    try {
      const data = await studentdbApi().get('/api/states');
      commit('setStates', Array.isArray(data) ? data : data.states || []);
    } finally {
      commit('setStatesLoading', false);
    }
  },

  async fetchCities({ commit, state }) {
    commit('setCitiesLoading', true);
    try {
      const params = buildFilterParams(state.studentFilter);
      const data = await studentdbApi().get('/api/cities', { params });
      commit('setCities', Array.isArray(data) ? data : data.result || []);
    } finally {
      commit('setCitiesLoading', false);
    }
  },

  async fetchStateDetail({ commit }, stateCode) {
    commit('setCurrentStateLoading', true);
    try {
      const data = await studentdbApi().get(`/api/states/${stateCode}`);
      commit('setCurrentState', data);
    } finally {
      commit('setCurrentStateLoading', false);
    }
  },

  async fetchSchoolDetail({ commit }, schoolId) {
    commit('setCurrentSchool', null);
    commit('setCurrentSchoolLoading', true);
    try {
      const data = await studentdbApi().get(`/api/schools/${schoolId}`);
      commit('setCurrentSchool', data?.result || null);
    } finally {
      commit('setCurrentSchoolLoading', false);
    }
  },

  async fetchVectorOffices({ commit, state }) {
    if (state.vectorOffices.length) return;

    commit('setVectorOfficesLoading', true);
    try {
      const data = await studentdbApi().get('api/offices');
      commit('setVectorOffices', data?.result || []);
    } finally {
      commit('setVectorOfficesLoading', false);
    }
  },

  async fetchSchools(
    { commit, state },
    {
      filters = {},
      sort = {},
      page = 1,
      itemsPerPage = 25,
      append = false,
      requestKey = null,
    }
  ) {
    commit('setSchoolsLoading', true);
    try {
      if (requestKey != null && !append) {
        commit('setSchoolsRequestKey', requestKey);
      }

      const offset = (page - 1) * itemsPerPage;
      const params = {
        ...filters,
        ...buildFilterParams(state.studentFilter),
        limit: itemsPerPage,
        offset,
      };

      const sortParam = buildSortParam(sort);
      if (sortParam) params.sort = sortParam;

      const data = await studentdbApi().get('/api/schools', { params });
      if (
        requestKey != null &&
        state.schoolsRequestKey != null &&
        requestKey !== state.schoolsRequestKey
      ) {
        return;
      }

      const schools = Array.isArray(data) ? data : data.schools || [];
      if (append) {
        const merged = [...(state.schools || []), ...schools];
        const uniqueById = merged.filter(
          (school, idx, arr) =>
            arr.findIndex(({ id }) => String(id) === String(school.id)) === idx
        );
        commit('setSchools', uniqueById);
      } else {
        commit('setSchools', schools);
      }
      commit('setSchoolsTotal', data?.total ?? schools.length);
    } finally {
      commit('setSchoolsLoading', false);
    }
  },

  async fetchSports({ commit, state }) {
    if (state.sports.length) return;

    commit('setSportsLoading', true);
    try {
      const data = await studentdbApi().get('/api/sports');
      commit('setSports', Array.isArray(data) ? data : data.result || []);
    } finally {
      commit('setSportsLoading', false);
    }
  },

  async fetchTeams({ commit }, { schoolId } = {}) {
    commit('setTeamsLoading', true);
    try {
      const params = { limit: 1000 };
      if (schoolId) params.schoolId = schoolId;
      const data = await studentdbApi().get('/api/teams', { params });
      const teams = Array.isArray(data) ? data : data.teams || [];
      commit('setTeams', teams);
    } finally {
      commit('setTeamsLoading', false);
    }
  },

  clearStudents({ commit }) {
    commit('clearStudents');
  },

  async fetchStudents(
    { commit, state },
    { filters = {}, sort = {}, page = 1, itemsPerPage = 25 }
  ) {
    commit('setStudentsLoading', true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const params = {
        ...filters,
        ...buildFilterParams(state.studentFilter),
        limit: itemsPerPage,
        offset,
      };

      const sortParam = buildSortParam(sort);
      if (sortParam) params.sort = sortParam;

      const data = await studentdbApi().get('/api/athletes', { params });
      const students = Array.isArray(data) ? data : data.athletes || [];
      commit('setStudents', students);
      commit('setStudentsTotal', data?.total ?? students.length);
    } finally {
      commit('setStudentsLoading', false);
    }
  },
};
